import React, { Fragment, useEffect } from "react";
import { Column, Container, Row } from "../components";
import { buildPathWithParams, useMediaQuery, useQueryString } from "../hooks";
import { ContactFormProvider } from "./contact_form_provider";
import { Heading } from "./heading";
import { Spacer } from "./spacer";
import { OfferingMessageType, useAllLocationsQuery } from "./api";
import { PartnerConfig } from "./partner_config";
import {
  CatalogUpsellTextAlign,
  CatalogUpsellVersionedImage,
  PartnerCatalog,
} from "../data/catalog";
import { useI18n } from "../lib/i18n/i18n_provider";
import { TemplateCatalogFlexible } from "./template_catalog_flexible";
import { TemplateCatalogOriginal } from "./template_catalog_original";
import { TemplateCatalogBlock } from "./template_catalog_block";
import { TemplateCatalogGallery } from "./template_catalog_gallery";
import { useLocationMaybeQuery } from "@/fm/api";
import { Button } from "react-bootstrap";
import { CatalogLocationFilter } from "./catalog_location_filter";
import { useCatalogLocationFilterFeatureFlag } from "@/core/splitio";
import {
  FilterLocation,
  getCatalogFilterLocations,
} from "@/data/partner_offering";
import { toFilterLocationFromLocation } from "@/data/partner_offering";
import { useHistory } from "react-router-dom";
import { Hero } from "@/offering_v2/components/hero";
import { HeroOverlay } from "@/offering_v2/components/hero_overlay";
import { ConditionalLink } from "./conditional_link";

interface CatalogBaseProps {
  partnerConfig: PartnerConfig;
  partnerCatalog: PartnerCatalog;
}

export function CatalogBase(props: CatalogBaseProps) {
  const { partnerCatalog, partnerConfig } = props;
  const { locationID } = useQueryString();
  const history = useHistory();
  const catalogLocationFilterFeatureFlag =
    useCatalogLocationFilterFeatureFlag();
  const { data: locationsData } = useAllLocationsQuery(
    partnerCatalog.partner.id
  );
  const mappedLocationsData = locationsData?.map(toFilterLocationFromLocation);
  const cityOrder = partnerConfig.getCalatogFilterCityOrder?.();
  if (mappedLocationsData && cityOrder) {
    mappedLocationsData.sort((a: FilterLocation, b: FilterLocation) => {
      const indexA = cityOrder.findIndex((cO) => a.city.includes(cO));
      const indexB = cityOrder.findIndex((cO) => b.city.includes(cO));
      if (indexA === -1) {
        return 1;
      } else if (indexB === -1) {
        return -1;
      }
      return indexA - indexB;
    });
  }

  const locations = getCatalogFilterLocations(
    mappedLocationsData,
    partnerCatalog
  );

  const location = useLocationMaybeQuery(locationID);

  useEffect(() => {
    if (
      locationID &&
      locations?.length &&
      !locations.find((l: FilterLocation) => l.id === locationID)
    ) {
      const pathWithParams = buildPathWithParams(window.location.pathname, {
        locationID: undefined,
      });
      history.replace(pathWithParams);
    }
  }, [history, location, locationID, locations]);

  const locationAdress = location
    ? location.streetAddress && location.city
      ? `${location.streetAddress} - ${location.city}${
          location.state ? `, ${location.state}` : ""
        }`
      : location.name
    : null;

  if (!partnerCatalog) {
    return (
      <Container>
        <Row justifyContent="center">
          <Heading size="md">
            Catalog Not Found, Please Contact Administrator
          </Heading>
        </Row>
      </Container>
    );
  }

  const { hubspotForm } = partnerConfig;
  const { id, partner, template } = partnerCatalog;
  const { id: partnerId } = partner;

  const isFlexibleTemplate = template === "flexible";
  const isOriginalTemplate = template === "original";
  const isBlockTemplate = template === "block";
  const isGalleryTemplate = template === "gallery";
  /**
   * `isDeprecatedFlatTemplate` is the unwanted/rollback case
   * Usually it was old data, has multiple catalogs,
   * one catalog is for membership
   * After running the migration, then we rollback
   * the all catalog templates to "flat"
   *
   * when the feature flag is removed
   * we will only check for the template type
   */
  const dataTestId = "general-catalog-container";

  const catalogLocationFilter =
    partnerCatalog.showLocationFilter &&
    locations &&
    catalogLocationFilterFeatureFlag &&
    locations.length > 1 ? (
      <CatalogLocationFilter
        partner={{
          id: partnerCatalog.partner.id,
          logoUrl: partnerCatalog.partner.logo,
          name: partnerCatalog.partner.name,
          internalName: partnerCatalog.partner.internalName,
          partnerSlug: partnerCatalog.partner.partnerSlug,
          homepageUrl: partnerCatalog.partner.homepageURL,
        }}
        locations={locations}
      />
    ) : null;

  return (
    <div
      data-testid={dataTestId}
      className={isOriginalTemplate ? "catalog-container" : ""}
    >
      <ContactFormProvider
        partnerID={partnerId}
        messageableID={id}
        messageableType={OfferingMessageType.PartnerCatalog}
        hubspotForm={hubspotForm}
      >
        {isFlexibleTemplate && (
          <TemplateCatalogFlexible
            partnerCatalog={partnerCatalog}
            partnerConfig={partnerConfig}
            locationAdress={locationAdress || ""}
            partnerName={partnerCatalog.partner.name}
            catalogLocationFilter={catalogLocationFilter}
            locations={locations}
          />
        )}
        {isOriginalTemplate && (
          <TemplateCatalogOriginal
            partnerCatalog={partnerCatalog}
            partnerConfig={partnerConfig}
            locationAdress={locationAdress || ""}
            catalogLocationFilter={catalogLocationFilter}
            locations={locations}
          />
        )}
        {isBlockTemplate && (
          <TemplateCatalogBlock
            partnerCatalog={partnerCatalog}
            partnerConfig={partnerConfig}
            locationAdress={locationAdress || ""}
            catalogLocationFilter={catalogLocationFilter}
            locations={locations}
          />
        )}
        {isGalleryTemplate && (
          <TemplateCatalogGallery
            partnerConfig={partnerConfig}
            partnerId={partnerId}
            partnerName={partnerCatalog.partner.name}
            locationAdress={locationAdress || ""}
          />
        )}
      </ContactFormProvider>
    </div>
  );
}

export interface UpsellCardProps {
  image: string;
  versionedImage?: CatalogUpsellVersionedImage;
  title: string;
  description: string;
  learnMoreURL: string;
  ctaLabel?: string;
  longDescription?: string;
  textAlign?: CatalogUpsellTextAlign;
  layout?: "hero" | "hero_overlay" | "default";
  onClickButton?: React.MouseEventHandler<
    HTMLAnchorElement | HTMLButtonElement
  >;
}

export function UpsellCard(props: UpsellCardProps) {
  const {
    image,
    versionedImage,
    title,
    description,
    learnMoreURL,
    ctaLabel,
    longDescription,
    layout,
    textAlign,
    onClickButton,
  } = props;
  const { fbt } = useI18n();
  const mq = useMediaQuery();

  const dataTestId = "catalog-upsell-card";

  if (layout === "hero") {
    return (
      <Hero
        title={title}
        description={description}
        longDescription={longDescription}
        imageUrl={versionedImage?.original.url || image}
        buttonLabel={ctaLabel}
        onClickButton={onClickButton}
        buttonUrl={learnMoreURL}
        textAlign={textAlign}
      />
    );
  } else if (layout === "hero_overlay") {
    return (
      <HeroOverlay
        title={title}
        description={description}
        longDescription={longDescription}
        imageUrl={versionedImage?.original.url || image}
        buttonLabel={ctaLabel}
        onClickButton={onClickButton}
        buttonUrl={learnMoreURL}
        textAlign={textAlign}
      />
    );
  }

  return (
    <Fragment>
      {mq.device === "desktop" ? (
        <div className="catalog-upsell-card" data-testid={dataTestId}>
          <Row>
            <Column flex={1}>
              <ConditionalLink href={learnMoreURL} onClick={onClickButton}>
                <img
                  alt={title}
                  style={{
                    objectFit: "cover",
                    width: "100%",
                    height: 320,
                    borderRadius: "4px 4px 0 0",
                  }}
                  src={versionedImage?.large.url || image}
                />
              </ConditionalLink>
            </Column>
          </Row>

          <Row alignItems="center">
            <Column>
              <Container padding={32} flex={1}>
                <h3>{title}</h3>
                <Spacer size={8} />
                {longDescription ? (
                  <div dangerouslySetInnerHTML={{ __html: longDescription }} />
                ) : (
                  <div>{description}</div>
                )}

                {learnMoreURL && (
                  <>
                    <Spacer size={24} />
                    <ConditionalLink
                      linkClassName="upsell-learn-more-button"
                      href={learnMoreURL}
                      onClick={onClickButton}
                    >
                      <Button variant="primary">
                        {ctaLabel || fbt("Learn more")}
                      </Button>
                    </ConditionalLink>
                  </>
                )}
              </Container>
            </Column>
          </Row>
        </div>
      ) : (
        <Container>
          <div className="catalog-upsell-card" data-testid={dataTestId}>
            <ConditionalLink href={learnMoreURL} onClick={onClickButton}>
              <img
                alt={title}
                style={{
                  objectFit: "cover",
                  width: "100%",
                  height: 240,
                  borderTopLeftRadius: 5,
                  borderTopRightRadius: 5,
                }}
                src={image}
              />
            </ConditionalLink>
            <Container flex={1} padding={24}>
              <h3>{title}</h3>
              {longDescription ? (
                <div dangerouslySetInnerHTML={{ __html: longDescription }} />
              ) : (
                <div>{description}</div>
              )}
              <Spacer size={16} />
              {learnMoreURL && (
                <ConditionalLink
                  linkClassName="upsell-learn-more-button"
                  href={learnMoreURL}
                  onClick={onClickButton}
                >
                  <Button style={{ width: "auto" }} variant="primary">
                    {ctaLabel || fbt("Learn more")}
                  </Button>
                </ConditionalLink>
              )}
            </Container>
          </div>
        </Container>
      )}
    </Fragment>
  );
}
